import { useEffect, useRef, useState } from "react";
import { login, ssoEnabled, startSso } from "../lib/auth.js";

/**
 * The network backdrop from the theme guide: points drifting across the
 * gradient, joined by a line whenever two come close. Drawn on a canvas
 * because the joins are recomputed every frame - there is no CSS shape for
 * "line between these two moving things".
 *
 * It stops entirely under reduce-motion: a still field of dots carries the
 * same look without the drift.
 */
function useNetwork(canvas: React.RefObject<HTMLCanvasElement | null>) {
  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx) return;

    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0, h = 0, raf = 0;
    type Dot = { x: number; y: number; vx: number; vy: number };
    let dots: Dot[] = [];

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = el.clientWidth; h = el.clientHeight;
      el.width = Math.round(w * dpr);
      el.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      /* Density by area, so a wide monitor does not end up sparse and a phone
         does not end up a solid mesh. */
      const n = Math.min(70, Math.max(24, Math.round((w * h) / 24000)));
      dots = Array.from({ length: n }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
      }));
    };

    const frame = () => {
      ctx.clearRect(0, 0, w, h);
      for (const d of dots) {
        if (!still) {
          d.x += d.vx; d.y += d.vy;
          if (d.x < 0 || d.x > w) d.vx *= -1;
          if (d.y < 0 || d.y > h) d.vy *= -1;
        }
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1.6, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,.22)";
        ctx.fill();
      }
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const a = dots[i]!, b = dots[j]!;
          const dx = a.x - b.x, dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist > 150) continue;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          /* Never above .16, per the guide - the card has to stay the thing
             you look at. */
          ctx.strokeStyle = `rgba(255,255,255,${(1 - dist / 150) * 0.16})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
      if (!still) raf = requestAnimationFrame(frame);
    };

    resize();
    frame();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [canvas]);
}

/**
 * The access gate. One shared password, no accounts - so this asks for the one
 * thing it needs and nothing else: no email field, no "sign up" leading
 * nowhere, no "forgot password" with no inbox to send to.
 */
/**
 * The callback redirects here with ?sso_error=... when Microsoft turned the
 * sign-in down - a different tenant, a cancelled consent, an expired attempt.
 * Reading it once and stripping it keeps the message off every later reload.
 */
function takeSsoError(): string {
  if (typeof window === "undefined") return "";
  const url = new URL(window.location.href);
  const msg = url.searchParams.get("sso_error");
  if (!msg) return "";
  url.searchParams.delete("sso_error");
  window.history.replaceState({}, "", url.toString());
  return msg;
}

export function Login({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  /* Off by default, as the Hotline login has it: these are shared office
     machines, so the token should die with the tab unless someone says
     otherwise. */
  const [keep, setKeep] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(takeSsoError);
  const [sso, setSso] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);

  useNetwork(canvas);

  /* Nothing else on the page can be used, so the caret starts in the field. */
  useEffect(() => { field.current?.focus(); }, []);

  /* The Microsoft button only appears once the server says it would work.
     Offering a button that answers 503 is worse than not offering one. */
  useEffect(() => {
    let cancelled = false;
    void ssoEnabled().then((on) => { if (!cancelled) setSso(on); });
    return () => { cancelled = true; };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !password) return;
    setBusy(true);
    setError("");
    try {
      await login(password, keep);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in.");
      /* Clear the field and put the caret back, so a retry is one keystroke
         away rather than a select-all first. */
      setPassword("");
      field.current?.focus();
      setBusy(false);
    }
  }

  return (
    <div className="jx-login">
      <canvas className="jx-login-net" ref={canvas} aria-hidden="true" />

      <div className="jx-login-card">
        <div className="jx-login-brand">
          <img
            className="jx-login-logo" src="/brand/happy-trails-logo.png"
            alt="Happy Trails Asia" width={440} height={184}
          />

          <span className="jx-login-kicker">
            <span className="jx-login-dot" aria-hidden="true" />
            Live from SharePoint
          </span>

          <h1 className="jx-login-h1">Data Entry<br />Masterfile</h1>
          <p className="jx-login-org">Happy Trails! Asia</p>
          <p className="jx-login-tag">Every rate, both Masterfiles &mdash; in one view.</p>
          <p className="jx-login-desc">
            Rate 27&ndash;28 data entry for Indonesia and Malaysia: progress,
            outstanding work and data-quality findings, updated automatically
            from SharePoint.
          </p>

          <div className="jx-login-chips">
            <span className="jx-login-chip"><b>ID</b> Indonesia</span>
            <span className="jx-login-chip"><b>MY</b> Malaysia</span>
            <span className="jx-login-chip"><b>27&ndash;28</b> Season</span>
          </div>
        </div>

        <div className="jx-login-form">
          <div className="jx-login-form-in">
            <h2 className="jx-login-h">Welcome back</h2>
            <p className="jx-login-sub">
              Enter the team password to open the dashboard.
            </p>

            <form onSubmit={submit} noValidate>
              <label className="jx-login-lab" htmlFor="jx-password">Password</label>
              <div className="jx-login-field">
                <i className="bi bi-lock jx-login-ic" aria-hidden="true" />
                <input
                  id="jx-password"
                  ref={field}
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="Enter the password"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? "jx-login-err" : undefined}
                />
                <button
                  type="button" className="jx-login-peek"
                  onClick={() => setShow((v) => !v)}
                  aria-label={show ? "Hide password" : "Show password"}
                  aria-pressed={show}
                >
                  <i className={`bi ${show ? "bi-eye-slash" : "bi-eye"}`} aria-hidden="true" />
                </button>
              </div>

              <p className="jx-login-err" id="jx-login-err" role="alert">
                {error ? (
                  <>
                    <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                    {" "}{error}
                  </>
                ) : null}
              </p>

              {/* Not decoration: unchecked, the token goes to sessionStorage
                  and the sign-in ends with the tab. */}
              <label className="jx-login-keep">
                <input
                  type="checkbox" checked={keep}
                  onChange={(e) => setKeep(e.target.checked)}
                />
                Keep me signed in
              </label>

              <button className="jx-login-btn" type="submit" disabled={busy || !password}>
                <i
                  className={`bi ${busy ? "bi-arrow-repeat spin" : "bi-box-arrow-in-right"}`}
                  aria-hidden="true"
                />
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>

            {sso && (
              <>
                <div className="jx-login-or"><span>or</span></div>

                {/* A link, not a fetch: the browser has to leave for Microsoft
                    and come back, which an XHR cannot do. */}
                <button
                  className="jx-login-ms" type="button"
                  onClick={() => startSso()} disabled={busy}
                >
                  <svg className="jx-ms-logo" viewBox="0 0 21 21" aria-hidden="true">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                  </svg>
                  Sign in with Microsoft
                </button>
              </>
            )}

            <p className="jx-login-foot">
              <i className="bi bi-shield-lock" aria-hidden="true" />
              Internal system &middot; Happy Trails Asia staff only
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
